class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.header = document.querySelector('header');
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
    this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this));
    this.form?.addEventListener('submit', this.onSubmit.bind(this));

    this.isHeaderSearch = this.closest('header') !== null;

    document.addEventListener('menu-drawer:open', () => {
      this.classList.remove('predictive-search-open');
      document.body.classList.remove('search-open');
    });

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get searchProductTypes() {
    return this.querySelector('#SearchProductTypes');
  }

  get form() {
    return this.querySelector('form');
  }

  onFocus(event) {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );
    if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
      document.body.classList.add('search-open');
      this.toggleNavButtonVisibility(false);
    }

    if (!this.searchRecommendationEmpty) {
      this.searchContent.classList.remove('hidden');
    }
    this.classList.add('predictive-search-open');
    if (this.getQuery().length === 0) {
      if (this.searchRecommendationEmpty) {
        this.searchContent.classList.add('hidden');
      }
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url, event);
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    /* ⚠ PG: an empty field means there is NO current query, so anything still in flight must
       not paint. Without this, a response for the query the shopper just abandoned landed a
       second later and rendered those results into the panel — and it was plainly visible,
       because [results] (set by that response's own finally) outranks the `hidden` class the
       pre-type panel puts on the results box: `.search__form[results] .search__results`
       (theme.css:8031) is a (0,3,0) selector against `.hidden`'s (0,1,0) at theme.css:4932,
       and <predictive-search> IS the .search__form. Measured: panel jumped 473px -> 620px
       showing "cleco" products under a field containing nothing.
       ⚠ The spinner is stopped by hand rather than through setLoadingState(false), which
       would set [results] and claim an answer that no longer exists. */
    this.requestSeq = (this.requestSeq || 0) + 1;
    this.removeAttribute('loading');
    this.resetButton.classList.remove(this.states.LOADING);
    this.toggleSearchState(false);
  }

  handleProductTypeChange(evt) {
    const query = this.getQuery();
    if (query.length > 0) {
      /* ⚠ PG: the ONE path that starts a request without the panel already being open — the
         type <select> sits in the field, and on /search the query is pre-filled from the URL,
         so the shopper can change it without ever focusing the input. renderSearchResults now
         refuses to reveal a panel that is not open (the OPEN guard below), and in stock the base theme that
         render WAS the only opener on this path — without this line the results would be
         written and then left invisible, i.e. the dropdown would do nothing.
         Dormant today (settings_data.json:89 predictive_search_type_enabled = false, so the
         <select> is not rendered and this listener never binds) but four theme presets in the
         same file set it true, so a preset swap in the editor arms it silently.
         The class only — not toggleSearchState(true) — so the body/nav-button side effects
         stay exactly where the base theme put them, in renderSearchResults just below. */
      this.classList.add(this.states.OPEN);
      const url = this.setupURL().toString();
      this.renderSection(url);
    }
  }

  onSubmit(event) {
    const productType = this.searchProductTypes?.value;
    if (!productType) return;
    const query = this.getQuery();
    if (query.length === 0) return;
    if (query.startsWith('product_type:')) return;
    event.preventDefault();
    const params = new URLSearchParams();
    params.set('q', `product_type:${productType} AND ${query}`);
    params.set('options[prefix]', 'last');
    window.location.href = `${this.form.getAttribute('action')}?${params.toString()}`;
  }

  setupURL() {
    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    let search_term = this.getQuery();
    if (this.searchProductTypes && this.searchProductTypes.value != '') {
      search_term = `product_type:${this.searchProductTypes.value} AND ${encodeURIComponent(search_term)}`;
    }
    return (
      url.searchParams.set('q', search_term),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      // Shopify's default fields are title,product_type,variants.title,vendor — SKU and barcode are
      // excluded, so typing a part number returned "no results" even though pressing enter found it.
      // barcode carries UPC/EAN, and ISBNs on the ASA textbooks.
      url.searchParams.set(
        'resources[options][fields]',
        'title,product_type,variants.title,vendor,variants.sku,variants.barcode'
      ),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    /* ⚠ PG: every request is numbered, INCLUDING the cache path. Responses were applied
       in the order they ARRIVED, not the order they were asked for, so a slow response
       for an older query overwrote a newer one — measured on the dev theme with the field
       reading "headset" while the panel showed rivet results. A cache hit renders
       immediately, so it must invalidate anything still in flight too. */
    this.requestSeq = (this.requestSeq || 0) + 1;
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText);
    /* ⚠ PG: setLoadingState(false) rather than a bare setAttribute('results'). A cache hit
       can land while an older fetch is still in flight, and that fetch no longer clears the
       loading state (it may be superseded), so the spinner would never stop. This sets
       [results] exactly as before, and additionally clears [loading] and the spinner. */
    this.setLoadingState(false);
  }

  renderSectionFromFetch(url) {
    this.setLoadingState(true);
    const seq = this.requestSeq;

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        // Cache even when superseded: the data is valid for ITS OWN query, and discarding
        // it would make that query re-fetch. Only the RENDER is conditional.
        this.cachedMap.set(url, responseText);
        if (seq !== this.requestSeq) return;
        this.renderSearchResults(responseText);
      })
      .catch((error) => {
        if (seq !== this.requestSeq) return;
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => {
        /* ⚠ PG: a superseded response must NOT clear the loading state. setLoadingState(false)
           sets [results], which tells the panel — and our loading skeleton, which hides as
           soon as [results] appears — that an answer is on screen, while the newer request is
           still in flight. Leave both to whichever request is actually current. */
        if (seq !== this.requestSeq) return;
        this.setLoadingState(false);
      });
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        targetElement.innerHTML = contentElement.innerHTML;

        /* ⚠ PG: never resurrect a panel the shopper has already dismissed. A response can
           land after a click on the overlay (handleClickOutside), after the mobile header
           collapses (header-mobile-search.js closePredictiveSearch, on Escape or focusout),
           or after the menu drawer opens (line 21-24). All three remove this one class, and
           re-adding it here threw the panel back over the page seconds later — re-locking
           page scroll with it. Measured on the dev theme: dismissed at 2.0s, reopened by the
           response at 6.0s; on mobile Escape at 3.5s, reopened at 7.4s.
           The markup above is still written and cached, so re-opening shows current results.
           ⚠ The class is the signal because it is the ONE thing all three closers write, and
           it is re-added on the next keystroke (onFocus line 62, wb-search.js typing()) — so
           clearing the field and typing again is not mistaken for a dismissal. */
        if (!this.classList.contains(this.states.OPEN)) return;

        this.searchContent?.classList.remove('hidden');

        if (this.isHeaderSearch && !document.body.classList.contains('search-open')) {
          document.body.classList.add('search-open');
          this.toggleNavButtonVisibility(true);
          this.classList.add('predictive-search-open');
        } else {
          this.classList.add('predictive-search-open');
        }
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  handleClickOutside(event) {
    const target = event.target;
    // the base theme's header close only fires on its own fixed overlay or close button.
    // With search recommendations empty (this store), onFocus never adds
    // `search-open` to body, so the overlay never opens and NOTHING could close
    // the search: `predictive-search-open` stuck on this element forever after
    // tapping away, recolouring the field scheme-navy (Owen, 2026-08-15). When
    // no overlay is up, any outside click closes - which changes nothing in the
    // overlay case, where outside clicks always land ON the overlay.
    const shouldClose = this.isHeaderSearch
      ? !this.contains(target) &&
        ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
          target.classList.contains('header__search-close') ||
          !document.body.classList.contains('search-open'))
      : !this.contains(target);

    if (shouldClose) {
      setTimeout(() => this.toggleSearchState(false));
    }
  }

  toggleSearchState(isOpen) {
    this.classList.toggle(this.states.OPEN, isOpen);
    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
      this.toggleNavButtonVisibility(!isOpen);
    }
    if (!isOpen && this.searchRecommendationEmpty) {
      this.searchContent?.classList.add('hidden');
    }
  }

  toggleNavButtonVisibility(isOpen) {
    this.toggleNavigationButton && this.toggleNavigationButton.classList.toggle('is-show', isOpen);
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButton.classList.add(this.states.LOADING);
    } else {
      this.removeAttribute('loading');
      this.resetButton.classList.remove(this.states.LOADING);
      this.setAttribute('results', 'true');
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);
